/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { observer } from "mobx-react-lite"
import type { ComponentProps } from "react"
import { useState } from "react"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { useViewModel } from "../app/app-hooks"

import "./app-icon.css"

const iconFallback = "/assets/iap-dash-icon.png"
const iconUrlPrefix = `${process.env.API_URL}/api/private/application-type/`

interface Props extends Omit<ComponentProps<"img">, "src" | "onError"> {
    /**
     * Application type ID. Without an ID, the fallback icon is rendered.
     */
    appTypeId: number | undefined
    /** Additional classes, merged with the component's. */
    className?: string
    /**
     * Accessible name for the icon.
     *
     * @default ""
     */
    alt?: string
}

/**
 * Renders an application type's icon, falling back to the placeholder when no
 * icon is available.
 *
 * Uploaded icons are cached by the server for a day, so the URL is versioned
 * with the number of uploads to ensure a new icon is fetched immediately.
 */
export const AppIcon = observer(({ appTypeId, alt = "", className, ...imageProps }: Props) => {
    // Remember which URL failed so a new version can retry automatically. Keeping
    // this in state also lets React own the fallback instead of mutating `img.src`
    // outside the rendered value.
    const [failedSrc, setFailedSrc] = useState<string | null>(null)

    const appTypeVM = useViewModel(ApplicationTypeViewModel)

    const version = appTypeId === undefined ? 0 : appTypeVM.iconVersionFor(appTypeId)
    const src =
        appTypeId === undefined
            ? iconFallback
            : `${iconUrlPrefix}${appTypeId}/icon${version === 0 ? "" : `?v=${version}`}`

    return (
        <img
            {...imageProps}
            src={src === failedSrc ? iconFallback : src}
            alt={alt}
            className={clsx("app-icon", className)}
            onError={() => setFailedSrc(src)}
        />
    )
})
